
import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, BookOpen, Link, Unlink } from 'lucide-react';

interface TextSelectionMenuProps {
  selectedText: string;
  onEnhanceText: () => void;
  editorRef: React.RefObject<HTMLDivElement>;
  editor?: any;
  onAddLink?: () => void;
}

export function TextSelectionMenu({ 
  selectedText, 
  onEnhanceText,
  editorRef,
  editor,
  onAddLink
}: TextSelectionMenuProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [savedSelection, setSavedSelection] = useState<{ from: number; to: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedText && selectedText.length > 0) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current?.getBoundingClientRect() || { top: 0, left: 0 };
        
        const top = rect.top - editorRect.top - 40;
        const left = rect.left - editorRect.left;
        
        setPosition({ top, left });
        setVisible(true);
      }
    } else {
      setVisible(false);
    }
  }, [selectedText, editorRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEnhanceClick = () => {
    setVisible(false);
    onEnhanceText();
  };

  const handleFormatText = (format: 'bold' | 'italic' | 'underline') => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);
    const currentColor = editor.getAttributes('textStyle').color;

    switch (format) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'underline':
        editor.chain().focus().toggleUnderline().run();
        break;
    }

    // Preserve text color after applying formatting
    if (currentColor && selectedText) {
      // Small delay to ensure the formatting is applied first
      setTimeout(() => {
        editor.chain().focus().setColor(currentColor).run();
      }, 10);
    }

    setVisible(false);
  };

  const handleAddLink = () => {
    if (!editor) return;

    // Save selection now — it will be gone by the time the modal is submitted
    const { from, to } = editor.state.selection;
    setSavedSelection({ from, to });

    const existingLink = editor.getAttributes('link');
    setLinkUrl(existingLink.href || '');
    setShowLinkModal(true);
    setVisible(false);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl || !editor) return;

    if (savedSelection && savedSelection.from !== savedSelection.to) {
      // Restore saved selection, then apply the link
      editor
        .chain()
        .focus()
        .setTextSelection({ from: savedSelection.from, to: savedSelection.to })
        .setLink({ href: linkUrl, target: '_blank', rel: 'noopener noreferrer' })
        .run();
    } else {
      // No saved selection — insert link text at current cursor
      editor.chain().focus().insertContent(
        `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkUrl}</a>`
      ).run();
    }

    setShowLinkModal(false);
    setLinkUrl('');
    setSavedSelection(null);
  };

  const handleRemoveLink = () => {
    if (!editor) return;

    editor.chain().focus().unsetLink().run();
    setVisible(false);
  };

  const isLinkActive = () => {
    if (!editor) return false;
    return editor.isActive('link');
  };

  return (
    <>
      {/* Floating toolbar — only shown when text is selected */}
      {visible && (
        <div
          ref={menuRef}
          className="absolute z-50 bg-white rounded-lg shadow-lg border border-sand/20 py-1 px-1 flex items-center gap-1"
          style={{
            top: `${Math.max(0, position.top)}px`,
            left: `${Math.max(0, position.left)}px`,
            transform: 'translate(0, -100%)'
          }}
        >
          <span className="text-xs text-slate px-2 border-r border-sand/20">
            {selectedText.split(/\s+/).filter(Boolean).length} words
          </span>
          <button
            onClick={handleEnhanceClick}
            className="flex items-center px-3 py-2 text-sm text-slate hover:bg-electric/10 hover:text-electric rounded-md"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Enhance Text
          </button>

          <div className="border-l border-sand/20 pl-1 flex items-center gap-1">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatText('bold')}
              className="p-2 text-slate hover:bg-electric/10 hover:text-electric rounded-md"
            >
              <strong>B</strong>
            </button>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatText('italic')}
              className="p-2 text-slate hover:bg-electric/10 hover:text-electric rounded-md"
            >
              <em>I</em>
            </button>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleFormatText('underline')}
              className="p-2 text-slate hover:bg-electric/10 hover:text-electric rounded-md"
            >
              <u>U</u>
            </button>

            <div className="border-l border-sand/20 ml-1 pl-1">
              {isLinkActive() ? (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleRemoveLink}
                  className="p-2 text-slate hover:bg-electric/10 hover:text-electric rounded-md"
                  title="Remove Link"
                >
                  <Unlink className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleAddLink}
                  className="p-2 text-slate hover:bg-electric/10 hover:text-electric rounded-md"
                  title="Add Link"
                >
                  <Link className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Link modal — rendered outside the visibility guard so it survives when the toolbar hides */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-semibold text-midnight mb-4">Add Link</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-1">URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-slate hover:text-midnight"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkSubmit}
                disabled={!linkUrl}
                className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
